import type { PreferenceTile } from "components/Screens/Marketplace/Customer/types";

export type AskNumber = 1 | 2 | 3;
export type AskStatus = "shown" | "dismissed";

export interface AskState {
  answered: boolean;
  asks: Partial<Record<AskNumber, AskStatus>>;
  /** What the customer told us; shown first in Edge recommend and the Business profile page. */
  stated: PreferenceTile[];
}

const EMPTY: AskState = { answered: false, asks: {}, stated: [] };
const keyFor = (customerId: string) => `plgPreferenceAsks:${customerId}`;

// Per-viewer UI convenience only (spec §6.4). The answers themselves go to talent-db.
export function loadAskState(customerId: string): AskState {
  try {
    const raw = JSON.parse(localStorage.getItem(keyFor(customerId)) || "null");
    return raw && typeof raw === "object" ? { ...EMPTY, ...raw, asks: { ...(raw.asks || {}) } } : { ...EMPTY, asks: {} };
  } catch {
    return { ...EMPTY, asks: {} };
  }
}

export function saveAskState(customerId: string, state: AskState): void {
  try {
    localStorage.setItem(keyFor(customerId), JSON.stringify(state));
  } catch {
    // storage blocked — asks may repeat, which is harmless
  }
}

export const withAsk = (s: AskState, n: AskNumber, status: AskStatus): AskState => ({ ...s, asks: { ...s.asks, [n]: status } });
export const withAnswer = (s: AskState, tiles: PreferenceTile[]): AskState => ({ ...s, answered: true, stated: tiles });

/**
 * Prototype maybeAskPref: ask 1 after the first save; if it was dismissed, ask 2 after the second (QA PO-3).
 * Never again once answered; ask 3 lives in the meeting request.
 */
export function nextPopupAsk(s: AskState, shortlistCount: number): 0 | 1 | 2 {
  if (s.answered) return 0;
  if (shortlistCount === 1 && !s.asks[1]) return 1;
  if (shortlistCount >= 2 && s.asks[1] === "dismissed" && !s.asks[2]) return 2;
  return 0;
}

/** Ask 3 only ever appears inside the Request interviews panel. */
export function shouldShowAsk3(s: AskState): boolean {
  return !s.answered && s.asks[1] === "dismissed" && s.asks[2] !== "shown" && s.asks[3] !== "dismissed";
}
